"use client";

import Linkify from "linkify-react";
import Link from "next/link";

import { toast } from "react-toastify";
import { MdContentCopy } from "react-icons/md";
import { FaAsterisk } from "react-icons/fa";

import { RouterOutputs } from "~/utils/api";
import { APP_URL, generateRandomName } from "~/utils/constants";
import { slugifyUser } from "~/utils/string";

import Badge from "~/components/v3/icons/Badge";
import { If } from "~/components/v3/Conditional";
import { Avatar, AvatarImage } from "~/components/v3/shadcn/Avatar";
import { Card, CardContent, CardHeader } from "~/components/v3/shadcn/Card";
import { SocialLinksComponent } from "~/components/v3/users-components/SocialLinksComponent";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { CommunitiesToHostOption } from "~/app/beta/events/create/actions";

import ProfileOptions from "./ProfileOptions";
import { UserEventList } from "./UserEventList";

type ProfileCardProps = {
    user: RouterOutputs["user"]["getProfile"];
    isCurrentUser: boolean;
    communities: CommunitiesToHostOption[];
};

const ProfileAndEvents = ({
    user,
    isCurrentUser,
    communities,
}: ProfileCardProps) => {
    const bannerImage = user.bannerImage ?? "bg-v3-N-400";
    const bannerStyle = bannerImage?.startsWith("http")
        ? {
              backgroundImage: `url(${bannerImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
          }
        : {
              background: bannerImage,
          };

    const copyProfileSlug = async () => {
        await navigator.clipboard.writeText(
            `${APP_URL}/profile/${slugifyUser(user)}`,
        );

        toast.success("Profile URL copied to clipboard: ");
    };

    return (
        <Card className="relative h-max w-full border-0 px-0 pt-0">
            <CardHeader className="relative h-[230px] w-full p-0">
                <div
                    style={bannerStyle}
                    className={`${bannerImage} absolute z-10 h-full w-full rounded-t-[10px]`}
                />

                <div className="z-20 flex h-full flex-col justify-end px-6 pb-4">
                    <UserAvatar user={user} className="h-24 w-24" />
                </div>
                <div className="absolute right-[10px] top-[10px] z-20">
                    <ProfileOptions
                        user={user}
                        isCurrentUser={isCurrentUser}
                        communities={communities}
                    />
                </div>
            </CardHeader>

            <CardContent className="flex flex-col gap-4 px-2 pt-3 sm:px-[15px]">
                <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap justify-between">
                        <div className="flex items-center gap-2 md:flex-row">
                            <h1 className="text-nowrap text-[24px]">
                                {user.name ?? generateRandomName(user.id)}
                            </h1>

                            <SocialLinksComponent
                                socialLinks={user.socialLinks}
                            />
                        </div>

                        <div
                            className="flex w-fit cursor-pointer items-center justify-center gap-3 rounded-[10px] bg-v3-N-500 p-2 px-4"
                            onClick={copyProfileSlug}
                        >
                            <span className="text-sm">
                                /{slugifyUser(user)}
                            </span>
                            <MdContentCopy />
                        </div>
                    </div>

                    <div className="flex gap-2 text-sm">
                        <div className="flex items-center gap-2">
                            <span>
                                {(
                                    user._count.followedByUsers +
                                    user._count.following
                                ).toLocaleString()}
                            </span>
                            <span>Followers</span>
                        </div>
                        <div className="flex items-center">
                            <FaAsterisk className="text-v3-N-300" size={9} />
                        </div>
                        <div className="flex items-center gap-2">
                            <span>
                                {(
                                    user._count.attendedEvents +
                                    user._count.hostedEvents
                                ).toLocaleString()}
                            </span>
                            <span>Events</span>
                        </div>
                    </div>

                    <If condition={!!user.organizationName}>
                        <div className="flex w-max gap-2">
                            {user.organizationLink ? (
                                <Link
                                    href={user.organizationLink}
                                    target="_blank"
                                    className="flex items-center gap-2 text-v3-N-300 hover:text-v3-primary"
                                >
                                    {user.organizationImage && (
                                        <Avatar className="h-[32px] w-[32px]">
                                            <AvatarImage
                                                src={user.organizationImage}
                                                alt="Organization Image"
                                                width={32}
                                                height={32}
                                            />
                                        </Avatar>
                                    )}
                                    {user.organizationName}
                                </Link>
                            ) : (
                                <div className="flex items-center gap-2">
                                    {user.organizationImage && (
                                        <Avatar className="h-[32px] w-[32px]">
                                            <AvatarImage
                                                src={user.organizationImage}
                                                alt="Organization Image"
                                                width={32}
                                                height={32}
                                            />
                                        </Avatar>
                                    )}
                                    <span className="text-v3-N-300">
                                        {user.organizationName}
                                    </span>
                                </div>
                            )}
                        </div>
                    </If>
                </div>

                <div className="flex flex-col gap-2">
                    <h2 className="text-base font-[400] text-v3-N300">About</h2>
                    <div
                        className="whitespace-pre-wrap break-words leading-normal"
                        style={{ wordBreak: "break-word" }}
                    >
                        <Linkify options={{ className: "text-secondary" }}>
                            {user.bio}
                        </Linkify>
                    </div>
                </div>

                <div className="flex w-full flex-col flex-wrap gap-2">
                    <If condition={!!user.badges.length}>
                        <h2 className="mt-4 text-base font-[400] text-v3-N300">
                            Badges
                        </h2>
                        <div className="flex flex-wrap items-center gap-2">
                            {user.badges.map((badge) => (
                                <Link
                                    className="shrink-0"
                                    key={badge.id}
                                    href={`/${badge.validCommunities.at(0)?.slug}`}
                                >
                                    <Badge size={42} badge={badge} />
                                </Link>
                            ))}
                        </div>
                    </If>

                    <div className="flex flex-col gap-2">
                        <h2 className="mt-4 text-base font-[400] text-v3-N300">
                            Events
                        </h2>

                        <UserEventList
                            user={{
                                id: user.id,
                                name: user.name ?? "",
                                image: user.image ?? "",
                            }}
                            isCurrentUser={isCurrentUser}
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default ProfileAndEvents;
